import { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api-client';
import { useStripeConfig } from '@/hooks/use-stripe-config';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import type { StripeConnectInstance } from '@stripe/connect-js';

export interface ConnectAccount {
    id: string;
    organization_id: string;
    application_id?: string;
    stripe_account_id: string;
    account_type: string;
    onboarding_status: string;
    charges_enabled: boolean;
    payouts_enabled: boolean;
    details_submitted: boolean;
    created: string;
    modified: string;
}

export type ConnectState =
    | { kind: 'loading' }
    | { kind: 'not_connected' }
    | { kind: 'onboarding'; account: ConnectAccount }
    | { kind: 'connected'; account: ConnectAccount }
    | { kind: 'error'; message: string };

export interface UseConnectStatusParams {
    statusUrl: string | null;
    accountSessionPayload: Record<string, string> | null;
}

export function useConnectStatus({ statusUrl, accountSessionPayload }: UseConnectStatusParams) {
    const { data: stripeConfig } = useStripeConfig();
    const [connectState, setConnectState] = useState<ConnectState>({ kind: 'loading' });
    const [stripeConnectInstance, setStripeConnectInstance] = useState<StripeConnectInstance | null>(null);

    const fetchConnectStatus = useCallback(async () => {
        if (!statusUrl) return;
        try {
            const { data } = await api.get<ConnectAccount>(statusUrl);
            if (data.details_submitted) {
                setConnectState({ kind: 'connected', account: data });
            } else {
                setConnectState({ kind: 'onboarding', account: data });
            }
        } catch (err: unknown) {
            const status = (err as { response?: { status?: number } })?.response?.status;
            if (status === 404) {
                setConnectState({ kind: 'not_connected' });
            } else {
                setConnectState({ kind: 'error', message: 'Failed to load billing status' });
            }
        }
    }, [statusUrl]);

    useEffect(() => {
        if (statusUrl) fetchConnectStatus();
    }, [statusUrl, fetchConnectStatus]);

    useEffect(() => {
        const account = connectState.kind === 'connected'
            ? connectState.account
            : connectState.kind === 'onboarding'
                ? connectState.account
                : null;

        if (!account?.stripe_account_id || !accountSessionPayload || !stripeConfig?.publishable_key) {
            setStripeConnectInstance(null);
            return;
        }

        const instance = loadConnectAndInitialize({
            publishableKey: stripeConfig.publishable_key,
            fetchClientSecret: async () => {
                const res = await api.post<{ client_secret: string }>(
                    '/billing/connect/account-session',
                    {
                        ...accountSessionPayload,
                        components: ['payments', 'payouts', 'account_onboarding', 'notification_banner'],
                    }
                );
                return res.data.client_secret;
            },
        });
        setStripeConnectInstance(instance);
    }, [
        connectState.kind,
        connectState.kind === 'connected' ? connectState.account.stripe_account_id : null,
        connectState.kind === 'onboarding' ? connectState.account.stripe_account_id : null,
        accountSessionPayload,
        stripeConfig?.publishable_key,
    ]);

    return { connectState, fetchConnectStatus, stripeConnectInstance };
}
